import { useEffect } from "react";
import { ShieldCheck, Phone, BellRing, CalendarClock, ShieldOff } from "lucide-react";
import PageHero from "../components/PageHero";
import QuoteForm from "../components/QuoteForm";
import ReminderForm from "../components/ReminderForm";
import { Reveal } from "../components/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { serviceFaqs } from "../data/faqs";
import { PHONE_TEL } from "../lib/site";

function ServicePage({ eyebrow, title, highlight, subtitle, image, cardsTitle, cardsIntro, cards, ctaLabel, defaultType, note, faqTitle, faqs, reminder = false }) {
  useEffect(() => {
    if (!faqs?.length) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [faqs]);

  return (
    <main data-testid={`${eyebrow.toLowerCase().replace(/[^a-z]+/g, "-")}-page`}>
      <PageHero eyebrow={eyebrow} title={title} highlight={highlight} subtitle={subtitle} image={image} />

      <section className="bg-ice py-24" data-testid="service-cards-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl" data-testid="service-cards-heading">
              {cardsTitle}
            </h2>
            {cardsIntro && (
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-charcoal/80">{cardsIntro}</p>
            )}
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.07}>
                <div className="group h-full rounded-3xl border border-navy/10 bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lift" data-testid={`cover-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-skycyan/15 to-mint/15">
                    <ShieldCheck className="h-6 w-6 text-mint-600" strokeWidth={1.9} />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold text-navy">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-charcoal/75">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          {note && (
            <Reveal delay={0.1}>
              <p className="mt-10 max-w-3xl text-xs leading-relaxed text-charcoal/60" data-testid="service-compliance-note">{note}</p>
            </Reveal>
          )}
        </div>
      </section>

      {faqs?.length > 0 && (
        <section className="bg-white py-24" data-testid="service-faq-section">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
            <div className="grid items-start gap-12 lg:grid-cols-[0.8fr_1.2fr]">
              <Reveal>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-skycyan-600">Common Questions</p>
                <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl" data-testid="service-faq-heading">
                  {faqTitle}
                </h2>
                <p className="mt-4 max-w-md text-base leading-relaxed text-charcoal/80">
                  Have a different question? Our team is happy to help over a quick call.
                </p>
                <a
                  href={PHONE_TEL}
                  className="mt-7 inline-flex items-center gap-2 rounded-full border-2 border-navy/15 px-7 py-3.5 text-sm font-bold text-navy transition-colors duration-200 hover:border-navy hover:bg-navy hover:text-white"
                  data-testid="service-faq-call-button"
                >
                  <Phone className="h-4 w-4" /> Ask Us Directly
                </a>
              </Reveal>
              <Reveal delay={0.12}>
                <Accordion type="single" collapsible className="space-y-4" data-testid="service-faq-accordion">
                  {faqs.map((f, i) => (
                    <AccordionItem
                      key={i}
                      value={`sfaq-${i}`}
                      className="rounded-2xl border border-navy/10 bg-ice px-6 transition-shadow duration-200 data-[state=open]:bg-white data-[state=open]:shadow-lift"
                      data-testid={`service-faq-item-${i}`}
                    >
                      <AccordionTrigger className="py-5 font-display text-base font-bold text-navy hover:no-underline [&>svg]:text-mint-600">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="pb-5 text-sm leading-relaxed text-charcoal/80">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {reminder && (
        <section id="reminder" className="relative overflow-hidden bg-navy py-24" data-testid="reminder-section">
          <div className="pointer-events-none absolute -left-24 top-0 h-80 w-80 rounded-full bg-mint/10 blur-3xl" />
          <div className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-skycyan/10 blur-3xl" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 md:px-12 lg:grid-cols-2">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-mint">Renewal Reminder</p>
              <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="reminder-heading">
                Policy expiring soon?
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-white/70">
                Leave your policy expiry date and contact details — we'll call you back right
                when your renewal is due, so your protection never lapses.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  { icon: BellRing, text: "A friendly reminder call before your due date" },
                  { icon: CalendarClock, text: "Takes less than a minute to set up" },
                  { icon: ShieldOff, text: "No spam — only your renewal reminder" },
                ].map((p) => (
                  <div key={p.text} className="flex items-center gap-3" data-testid={`reminder-point-${p.text.slice(0, 12).toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10">
                      <p.icon className="h-5 w-5 text-mint" />
                    </span>
                    <span className="text-sm font-medium text-white/80">{p.text}</span>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="rounded-[2rem] bg-white p-7 shadow-lift sm:p-10">
                <ReminderForm />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="bg-ice py-24" data-testid="service-quote-section">
        <div className="mx-auto grid max-w-7xl items-start gap-14 px-5 sm:px-8 md:px-12 lg:grid-cols-[1fr_1.2fr]">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-skycyan-600">Next Step</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl" data-testid="service-quote-heading">
              {ctaLabel}
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-charcoal/80">
              Share a few details and our team will reach out with guidance on suitable options.
            </p>
            <a
              href={PHONE_TEL}
              className="mt-8 inline-flex items-center gap-2 rounded-full border-2 border-navy/15 px-7 py-3.5 text-sm font-bold text-navy transition-colors duration-200 hover:border-navy hover:bg-navy hover:text-white"
              data-testid="service-call-button"
            >
              <Phone className="h-4 w-4" /> Prefer to talk? Call us
            </a>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-[2rem] border border-navy/10 bg-white p-7 shadow-soft sm:p-10">
              <QuoteForm defaultType={defaultType} />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

const COMPLIANCE =
  "PocketBima provides insurance solutions and assistance. Policy availability, coverage, premiums, terms and conditions are subject to the respective insurer's policy terms and applicable regulations.";

export function MotorInsurance() {
  return (
    <ServicePage
      eyebrow="Motor Insurance"
      title="Keep Moving."
      highlight="Stay Protected."
      subtitle="Insurance solutions for your car, bike, taxi and commercial vehicles."
      image="/images/motor.webp"
      cardsTitle="Motor Insurance Solutions"
      cards={[
        { title: "Car Insurance", desc: "Protection for your personal vehicle." },
        { title: "Bike Insurance", desc: "Coverage designed for two-wheelers." },
        { title: "Taxi Insurance", desc: "Insurance solutions for taxi and passenger vehicles." },
        { title: "GCV Insurance", desc: "Insurance solutions for goods-carrying commercial vehicles." },
        { title: "Third Party Insurance", desc: "Coverage for third-party legal liability as applicable." },
      ]}
      ctaLabel="Get Motor Insurance Quote"
      defaultType="Car Insurance"
      note={COMPLIANCE}
      reminder
      faqTitle="Motor Insurance FAQs"
      faqs={serviceFaqs.motor}
    />
  );
}

export function HealthInsurance() {
  return (
    <ServicePage
      eyebrow="Health Insurance"
      title="Your Health."
      highlight="Your Biggest Asset."
      subtitle="Explore health insurance solutions designed to help you prepare for unexpected medical expenses."
      image="/images/health.webp"
      cardsTitle="Health Insurance Solutions"
      cards={[
        { title: "Individual Health Insurance", desc: "Coverage designed around your personal health needs." },
        { title: "Family Health Insurance", desc: "Protection for your entire family under one plan." },
        { title: "Group Health Insurance", desc: "Health coverage solutions for teams and employees." },
        { title: "Group Personal Accident Insurance", desc: "Accident protection solutions for groups and organisations." },
      ]}
      ctaLabel="Explore Health Insurance"
      defaultType="Health Insurance"
      note={COMPLIANCE}
      reminder
      faqTitle="Health Insurance FAQs"
      faqs={serviceFaqs.health}
    />
  );
}

export function LifeInsurance() {
  return (
    <ServicePage
      eyebrow="Life & Term Insurance"
      title="Protect Their"
      highlight="Tomorrow."
      subtitle="Life and term insurance solutions designed to help you plan for your family's financial protection."
      image="/images/life.webp"
      cardsTitle="Life & Term Insurance Solutions"
      cards={[
        { title: "Life Insurance", desc: "Long-term financial protection planning for your loved ones." },
        { title: "Term Insurance", desc: "Simple, focused protection for a chosen period of life." },
        { title: "Group Term Insurance", desc: "Term protection solutions for groups and employees." },
      ]}
      ctaLabel="Talk to an Expert"
      defaultType="Term Insurance"
      note={COMPLIANCE}
      reminder
      faqTitle="Life & Term Insurance FAQs"
      faqs={serviceFaqs.life}
    />
  );
}

export function BusinessInsurance() {
  return (
    <ServicePage
      eyebrow="Business Insurance"
      title="Protect Your Business."
      highlight="Protect Your Hard Work."
      subtitle="Insurance solutions for businesses, employees, assets, goods and commercial risks."
      image="/images/business.webp"
      cardsTitle="Business Insurance Solutions"
      cardsIntro="From a neighbourhood shop to a growing factory, every business has something worth protecting — premises, stock, machinery, employees and goods on the move. Pocket Bima Insurance Service helps business owners in Greater Noida West, Orai and across Uttar Pradesh understand which covers matter for their specific work, in plain language, before they commit to any policy."
      cards={[
        { title: "Fire Insurance", desc: "Protection for your business property and assets against fire-related risks." },
        { title: "Workmen Compensation Insurance", desc: "Coverage solutions related to employee workplace risks, as applicable." },
        { title: "Marine Insurance", desc: "Protection for goods in transit and cargo-related risks." },
        { title: "Single Transit Insurance", desc: "Coverage designed for a specific, one-time transit of goods." },
        { title: "Group Insurance Solutions", desc: "Health, accident and term solutions for your team." },
      ]}
      ctaLabel="Protect My Business"
      defaultType="Fire Insurance"
      note={COMPLIANCE}
      faqTitle="Business Insurance FAQs"
      faqs={serviceFaqs.business}
    />
  );
}

export function TravelInsurance() {
  return (
    <ServicePage
      eyebrow="Travel Insurance"
      title="Travel More."
      highlight="Worry Less."
      subtitle="Explore travel insurance options designed to provide protection during your journey."
      image="/images/travel.webp"
      cardsTitle="Travel Insurance — What Your Journey Needs"
      cardsIntro="A delayed flight, a lost bag or a sudden hospital visit abroad can turn a dream trip into an expensive problem. Pocket Bima Insurance Service helps travellers from Greater Noida West and Orai compare travel insurance options for domestic holidays, international vacations, student travel and business trips — so you can pack with peace of mind."
      cards={[
        { title: "Journey Protection", desc: "Coverage options designed for unexpected events during your trip." },
        { title: "Medical Assistance", desc: "Options that can help with medical emergencies while travelling." },
        { title: "Baggage & Documents", desc: "Protection options for baggage and travel document mishaps." },
      ]}
      ctaLabel="Explore Travel Insurance"
      defaultType="Travel Insurance"
      note={COMPLIANCE}
      faqTitle="Travel Insurance FAQs"
      faqs={serviceFaqs.travel}
    />
  );
}
